import { createApiClient } from "@esi/api/client";
import {
  BUILD_ID_HEADER,
  clientOutdatedResponseSchema,
  DATABASE_UNAVAILABLE,
  sessionResponseSchema,
} from "@esi/shared";
import { BUILD_ID, markOutdated, observeBuild, trackRequest } from "./shell/build-refresh.ts";
import { markIdpRefreshDue } from "./shell/idp-refresh.ts";
import {
  isSessionEnded,
  isSigningOut,
  markSessionEnded,
  markSessionRenewed,
  showSessionEnded,
} from "./shell/session-ended.ts";
import { reportFailedRequest } from "./telemetry/client-telemetry.ts";

export { BUILD_ID } from "./shell/build-refresh.ts";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
/** Idempotent reads, repeated once on `503 database_unavailable` (openspec change proxy-pinning-fix). */
const RETRIED_METHODS = new Set(["GET", "HEAD"]);
/** The longest wait before that repeat, whatever `Retry-After` says. */
const MAX_RETRY_DELAY_MS = 2_000;

const methodOf = (input: RequestInfo | URL, init?: RequestInit) =>
  (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();

/** The wait before repeating a read: `Retry-After` in seconds (default 1), capped at 2 s. */
export function retryDelayMs(retryAfter: string | null): number {
  const seconds = retryAfter !== null && /^\d+$/.test(retryAfter.trim()) ? Number(retryAfter) : 1;
  return Math.min(seconds * 1000, MAX_RETRY_DELAY_MS);
}

/** A JSON error body's `code`, read from a clone (the caller still reads the body). */
async function bodyCode(res: Response): Promise<unknown> {
  const body: unknown = await res
    .clone()
    .json()
    .catch(() => null);
  return body !== null && typeof body === "object" ? (body as { code?: unknown }).code : undefined;
}

/**
 * For an action the person started (a send): once the session ended it brings the banner back
 * and says the action must not go ahead, so the caller keeps what was typed. It also checks for
 * a sign-in elsewhere: then the banner goes and the next attempt (with the refreshed session's
 * token) goes through. No automatic resend: it would race that token and repeated presses.
 */
export function blockedBySessionEnd(): boolean {
  if (!isSessionEnded()) {
    return false;
  }
  showSessionEnded();
  void confirmSession();
  return true;
}

/** Every API call (RPC client and chat transport) goes through here. */
export const apiFetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
  // Once the session ended, nothing but the session check leaves the browser: the call gets a
  // local 401, and the check tells whether a sign-in elsewhere made the session live again.
  if (isSessionEnded() && requestPath(input) !== SESSION_PATH) {
    // A change the person asked for (not a poll, retry or refetch) brings back a dismissed banner.
    if (!SAFE_METHODS.has(methodOf(input, init))) {
      showSessionEnded();
    }
    void confirmSession();
    return new Response(JSON.stringify({ code: "unauthorized" }), {
      status: 401,
      headers: { "content-type": "application/json" },
    });
  }
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : {}));
  headers.set(BUILD_ID_HEADER, BUILD_ID);
  const method = methodOf(input, init);
  const started = performance.now();
  // A call that never got an answer: a network error or a timeout (never one the page aborted).
  const unanswered = (error: unknown) => {
    const name = (error as { name?: unknown } | null)?.name;
    if (name !== "AbortError") {
      reportFailedRequest({
        method,
        path: requestPath(input),
        failure: name === "TimeoutError" ? "timeout" : "network",
        durationMs: performance.now() - started,
      });
    }
    throw error;
  };
  let res = await trackRequest(fetch(input, { ...init, headers })).catch(unanswered);
  // The database was briefly unreachable (`503 database_unavailable`): a read is repeated once
  // after the server's `Retry-After`, capped. Never a write, never twice, never another 503
  // (`model_unavailable` and `schema_not_ready` are 503s too): the body code decides, so a
  // bodiless HEAD answer is returned as is.
  if (
    res.status === 503 &&
    RETRIED_METHODS.has(method) &&
    (await bodyCode(res)) === DATABASE_UNAVAILABLE
  ) {
    await new Promise((resolve) =>
      setTimeout(resolve, retryDelayMs(res.headers.get("Retry-After"))),
    );
    res = await trackRequest(fetch(input, { ...init, headers })).catch(unanswered);
  }
  // A server failure (5xx): counted for the build; 4xx are expected outcomes, never reported.
  if (res.status >= 500) {
    reportFailedRequest({
      method,
      path: requestPath(input),
      failure: res.status,
      durationMs: performance.now() - started,
    });
  }
  // A newer build on the server: the tab picks it up at a safe point (build-refresh.ts).
  void observeBuild(res.headers.get(BUILD_ID_HEADER));
  // The session ended under the app (expired, signed out elsewhere): offer to sign in again.
  // The auth routes answer for themselves (the session check is never a 401).
  if (res.status === 401 && !isSigningOut() && !requestPath(input).startsWith("/api/auth/")) {
    void confirmSession();
  }
  if (res.status === 409) {
    const body = clientOutdatedResponseSchema.safeParse(
      await res
        .clone()
        .json()
        .catch(() => null),
    );
    // The call still fails as before; the reload waits for nothing to be held.
    if (body.success) {
      void markOutdated(res.headers.get(BUILD_ID_HEADER));
    }
  }
  return res;
}) as typeof fetch;

/**
 * A 401 is an ended session or a stale CSRF token (a sign-in in another tab rotated it): the
 * session check tells them apart. A live session only needs its token refreshed.
 */
let confirming: Promise<void> | null = null;
export function confirmSession(): Promise<void> {
  // A burst of 401s shares one check.
  confirming ??= checkSession({ after401: true }).finally(() => {
    confirming = null;
  });
  return confirming;
}

/**
 * The background check of a visible tab: only an ended session matters. A live one changes
 * nothing (no renewal, so no session refetch that would count as activity).
 */
export function watchSession(): Promise<void> {
  return confirming ?? checkSession({ after401: false });
}

async function checkSession(options: { after401: boolean }): Promise<void> {
  try {
    // A probe, not activity: it must not keep an idle session alive (`?probe=1`).
    const res = await fetch(`${SESSION_PATH}?probe=1`, {
      headers: { [BUILD_ID_HEADER]: BUILD_ID },
    });
    // The visible tab's check is how an idle tab learns of a new build (detection only; the
    // session answer never waits for it).
    void observeBuild(res.headers.get(BUILD_ID_HEADER));
    if (!res.ok) {
      // The server is struggling, not the session: the failing call reports its own error.
      return;
    }
    const body = sessionResponseSchema.safeParse(await res.json());
    if (!body.success) {
      return;
    }
    if (body.data.authenticated) {
      markIdpRefreshDue(body.data.idpRefreshDue);
      if (options.after401) {
        markSessionRenewed();
      }
    } else {
      markSessionEnded(body.data.ended ?? "expired");
    }
  } catch {
    // Unreachable: the failing call already says the server could not be reached.
  }
}

const SESSION_PATH = "/api/auth/session";

function requestPath(input: RequestInfo | URL): string {
  const href = input instanceof Request ? input.url : String(input);
  return new URL(href, "https://esi.invalid").pathname;
}

export const api = createApiClient("/", { fetch: apiFetch });
